import { ALL_SECTORS, sectorsFor, type Coord } from '../data/communications'
import { getActiveCity } from './activeCity'

/** One dwelling shown as a dot on the audience map. */
export interface Household {
  id: string
  coord: Coord
  address: string
  /** Number of registered bciti+ accounts at this address. */
  accounts: number
}

/** Streets used to label generated households, per sector. */
const STREETS: Record<string, string[]> = {
  abe: ['boulevard de Rome', 'avenue Panama', 'rue de Naples'],
  cijlmxy: [
    'boulevard Lapinière',
    'boulevard Leduc',
    'avenue Stravinski',
    'rue Nobel',
  ],
  nos: ['boulevard Taschereau', 'rue Alfred', 'avenue Auteuil'],
  prtv: ['boulevard Marie-Victorin', 'boulevard Grande-Allée', 'rue Pelletier'],
}

const FALLBACK_STREETS = ['boulevard Taschereau', 'boulevard de Rome']

/** Deterministic pseudo-random generator so dots never move between renders. */
function seeded(seed: number) {
  let state = seed >>> 0
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0
    return state / 0x100000000
  }
}

/** Standard ray-casting test. */
function inPolygon(point: Coord, polygon: Coord[]): boolean {
  let inside = false
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [latI, lngI] = polygon[i]
    const [latJ, lngJ] = polygon[j]
    const intersects =
      lngI > point[1] !== lngJ > point[1] &&
      point[0] < ((latJ - latI) * (point[1] - lngI)) / (lngJ - lngI) + latI
    if (intersects) inside = !inside
  }
  return inside
}

function hashOf(value: string): number {
  let hash = 2166136261
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 0
}

/**
 * Generates a stable set of dwellings scattered inside a sector's polygon.
 * These stand in for the address register so the user can confirm, dot by
 * dot, which homes a zone actually covers.
 */
export function householdsInSector(sectorId: string): Household[] {
  const sector = ALL_SECTORS.find((s) => s.id === sectorId)
  if (!sector) return []

  const lats = sector.polygon.map((p) => p[0])
  const lngs = sector.polygon.map((p) => p[1])
  const minLat = Math.min(...lats)
  const maxLat = Math.max(...lats)
  const minLng = Math.min(...lngs)
  const maxLng = Math.max(...lngs)

  const random = seeded(hashOf(sectorId))
  const streets = STREETS[sectorId] ?? FALLBACK_STREETS
  const households: Household[] = []

  // Rejection-sample until the sector holds a readable number of dots.
  let attempts = 0
  while (households.length < 44 && attempts < 900) {
    attempts++
    const coord: Coord = [
      minLat + random() * (maxLat - minLat),
      minLng + random() * (maxLng - minLng),
    ]
    if (!inPolygon(coord, sector.polygon)) continue

    const street = streets[Math.floor(random() * streets.length)]
    const civic = 1000 + Math.floor(random() * 8000)
    households.push({
      id: `${sectorId}-${households.length}`,
      coord,
      address: `${civic} ${street}`,
      accounts: 1 + Math.floor(random() * 3),
    })
  }

  return households
}

/** Metres between two coordinates, good enough at municipal scale. */
export function distanceBetween(a: Coord, b: Coord): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180
  const earth = 6371000
  const dLat = toRad(b[0] - a[0])
  const dLng = toRad(b[1] - a[1])
  const lat = toRad((a[0] + b[0]) / 2)
  const x = dLng * Math.cos(lat)
  return Math.sqrt(dLat * dLat + x * x) * earth
}

/**
 * Dwellings within a radius of a point. Households are drawn from every
 * sector so a radius straddling two districts still shows both.
 */
export function householdsNear(center: Coord, radius: number): Household[] {
  return sectorsFor(getActiveCity()).flatMap((sector) => householdsInSector(sector.id)).filter(
    (household) => distanceBetween(center, household.coord) <= radius,
  )
}
